import React, { useMemo } from 'react';
import { 
    Box, 
    useTheme,
    useMediaQuery,
    Skeleton,
    Card,
    Fade
} from '@mui/material';
import { styled } from '@mui/material/styles';
import dayjs from 'dayjs';
import DayColumn from './DayColumn';

// Desktop layout
const DesktopWeekContainer = styled(Box)(({ theme, height }) => ({
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(280px, calc(100vw - 48px)), 1fr))',
    gap: theme.spacing(2),
    maxWidth: '100%',
    overflow: 'hidden',
    [theme.breakpoints.down('lg')]: {
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(250px, calc(100vw - 32px)), 1fr))',
        gap: theme.spacing(1),
    },
    [theme.breakpoints.down('md')]: {
        gridTemplateColumns: '1fr',
        gap: theme.spacing(1),
    },
    ...(height && {
        height,
        minHeight: '600px',
        [theme.breakpoints.down('md')]: {
            height: 'auto',
            minHeight: '400px',
        },
    }),
}));

// Mobile layout
const MobileWeekContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
}));

const LoadingContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
}));

/**
 * WeekGrid Component
 * 
 * Handles the layout and organization of posts by day within a week.
 * Supports both mobile and desktop layouts with loading states.
 * 
 * @param {Object} props
 * @param {Array} props.posts - Array of all posts
 * @param {Object} props.currentWeek - dayjs object representing current week
 * @param {Function} props.onPostClick - Callback when a post is clicked
 * @param {boolean} props.loading - Whether posts are being loaded
 * @param {string} props.height - Height constraint for desktop layout
 * @param {boolean} props.showEmptyDays - Whether to show days with no posts
 */
const WeekGrid = ({ 
    posts = [], 
    currentWeek, 
    onPostClick, 
    loading = false,
    height = 'calc(100vh - 200px)',
    showEmptyDays = false
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    
    // Calculate week boundaries
    const weekStart = currentWeek.startOf('isoWeek');
    const weekEnd = currentWeek.endOf('isoWeek');

    // Group posts by day
    const postsByDay = useMemo(() => {
        const grouped = {};
        
        posts.forEach(post => {
            // Use display_date if available (from optimized calendar API), otherwise fall back to legacy logic
            const postDate = post.display_date ? 
                dayjs(post.display_date).format('YYYY-MM-DD') :
                dayjs(post.created_at || post.scheduled_post_date).format('YYYY-MM-DD');
                
            if (!grouped[postDate]) {
                grouped[postDate] = [];
            }
            grouped[postDate].push(post);
        });

        return grouped;
    }, [posts]);

    // Get days to display
    const daysToShow = useMemo(() => {
        if (showEmptyDays) {
            // Show all 7 days of the week
            const days = [];
            for (let i = 0; i < 7; i++) {
                const date = weekStart.add(i, 'day').format('YYYY-MM-DD');
                days.push({
                    date,
                    posts: postsByDay[date] || []
                });
            }
            return days;
        } else {
            // Only show days with posts
            return Object.keys(postsByDay)
                .filter(date => {
                    const postDate = dayjs(date);
                    return postDate.isSameOrAfter(weekStart, 'day') && 
                           postDate.isSameOrBefore(weekEnd, 'day');
                })
                .sort()
                .map(date => ({
                    date,
                    posts: postsByDay[date]
                }));
        }
    }, [postsByDay, weekStart, weekEnd, showEmptyDays]);

    // Render loading skeletons
    const renderLoadingSkeletons = () => {
        const skeletonCount = isMobile ? 2 : Math.min(daysToShow.length || 3, 7);
        
        if (isMobile) {
            return (
                <LoadingContainer>
                    {Array.from({ length: skeletonCount }).map((_, index) => (
                        <Card key={index} sx={{ mb: 2 }}>
                            <Box sx={{ p: 2 }}>
                                <Skeleton variant="text" width="40%" height={32} />
                                <Skeleton variant="text" width="30%" height={24} />
                            </Box>
                            <Box sx={{ p: 1, display: 'flex', gap: 1 }}>
                                {Array.from({ length: 2 }).map((_, cardIndex) => (
                                    <Skeleton
                                        key={cardIndex}
                                        variant="rectangular"
                                        width={280}
                                        height={120}
                                        sx={{ borderRadius: 1, flexShrink: 0 }}
                                    />
                                ))}
                            </Box>
                        </Card>
                    ))}
                </LoadingContainer>
            );
        }

        return (
            <DesktopWeekContainer height={height}>
                {Array.from({ length: skeletonCount }).map((_, index) => (
                    <Card key={index} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ 
                            p: 2, 
                            textAlign: 'center', 
                            borderBottom: 1, 
                            borderColor: 'divider',
                            backgroundColor: 'grey.50',
                            flexShrink: 0
                        }}>
                            <Skeleton variant="text" width="60%" height={32} sx={{ mx: 'auto', mb: 1 }} />
                            <Skeleton variant="text" width="40%" height={24} sx={{ mx: 'auto' }} />
                        </Box>
                        <Box sx={{ 
                            flex: 1, 
                            p: 1, 
                            display: 'flex', 
                            flexDirection: 'column', 
                            gap: 1,
                            overflowY: 'auto'
                        }}>
                            {Array.from({ length: 4 }).map((_, cardIndex) => (
                                <Skeleton
                                    key={cardIndex}
                                    variant="rectangular"
                                    height={120}
                                    sx={{ borderRadius: 1, flexShrink: 0 }}
                                />
                            ))}
                        </Box>
                    </Card>
                ))}
            </DesktopWeekContainer>
        );
    };

    // Show loading state
    if (loading) {
        return (
            <Fade in={loading}>
                <Box>
                    {renderLoadingSkeletons()}
                </Box>
            </Fade>
        );
    }

    // No posts to display
    if (daysToShow.length === 0) {
        return null;
    }

    // Render the appropriate layout
    if (isMobile) {
        return (
            <MobileWeekContainer>
                {daysToShow.map(({ date, posts }) => (
                    <DayColumn
                        key={date}
                        date={date}
                        posts={posts}
                        onPostClick={onPostClick}
                        isMobile={true}
                    />
                ))}
            </MobileWeekContainer>
        );
    }

    return (
        <DesktopWeekContainer height={height}>
            {daysToShow.map(({ date, posts }) => (
                <DayColumn
                    key={date}
                    date={date}
                    posts={posts}
                    onPostClick={onPostClick}
                    isMobile={false}
                />
            ))}
        </DesktopWeekContainer>
    );
};

export default WeekGrid; 